import { Chat, ChatType, Settings } from "../Shared/GeneratedTypes";
import {} from "../Shared/sharedTypes";
import * as actions from "./liveSwitchActions";
import * as types from "./liveSwitchActionTypes";
import liveSwitchApp from "./LiveSwitchApp";
import { LiveSwitchMiddlewareConnector } from "./liveSwitchMiddlewareConnector";

export function liveSwitchMiddleware(store: any) {
    const { dispatch, getState } = store;

    if (!liveSwitchApp) {
        return (next: any) => (action: any) => {
            next(action);
        };
    }

    const messageReceived = (client: fm.liveswitch.ClientInfo, message: string) => {
        const chat = JSON.parse(message.substring(5)) as Chat;
        const state = getState();
        const props = getUserChatProperties(client, chat);

        if (
            props.isPublic ||
            (!props.isExternal && (props.isInternal || props.isPrivate)) ||
            props.isClient ||
            props.isExternalObserver
        ) {
            dispatch(actions.chatReceived(chat, state.localizations?.chatReceived ?? "Chat Received"));
        }
    };

    function getUserChatProperties(client: fm.liveswitch.ClientInfo, chat: Chat): UserChatProperties {
        const state = getState();
        const accessInfo = state.accessInfo;
        const contact = state.order.contact;
        const eo = state.extObserverSession;

        const props = {
            isPublic: chat.chatType === ChatType.Public,
            isMentor: typeof state.mentor === "undefined",
            isClient:
                accessInfo?.firstName === contact.firstName &&
                accessInfo?.lastName === contact.lastName &&
                chat.chatType === ChatType.Mentee,
            isExternalObserver: !!eo && (eo.observerSessionId == chat.toId || eo.observerSessionId == chat.fromId),
            isInternal: chat.chatType === ChatType.Internal,
            isExternal: typeof state.mentor !== "undefined",
            isPrivate: chat.chatType === ChatType.Private,
        } as UserChatProperties;

        props.isClient = props.isClient && !props.isMentor;
        props.isExternalObserver = props.isExternalObserver && !props.isMentor;

        return props;
    }

    interface UserChatProperties {
        isMentor: boolean;
        isPublic: boolean;
        isClient: boolean;
        isExternalObserver: boolean;
        isInternal: boolean;
        isExternal: boolean;
        isPrivate: boolean;
    }

    let guideStreamSettings = (window as any).GuideStream.settings as Settings;

    var connector = {
        imageStillForAI: (image, date) => dispatch(actions.imageStillForAIReceived(image, date, false)),
        connectionStatusReceived: (value, deviceId) => dispatch(actions.connectionQualityPing(value, deviceId)),
        onPremiseGatewayUrl: guideStreamSettings.onPremiseGatewayUrl!,
        liveSwitchAppId: guideStreamSettings.liveSwitchAppId!,
        nonRecordingLiveSwitchAppId: guideStreamSettings.nonRecordingAppId!,
        localVideoDeviceListCallback: (deviceList) => dispatch(actions.localVideoDeviceListReceived(deviceList)),
        remoteVideoDeviceListCallback: (deviceList) => dispatch(actions.remoteVideoDeviceListReceived(deviceList)),
        audioDeviceListCallback: (deviceList) => dispatch(actions.localAudioInputListReceived(deviceList)),
        audioOutputDeviceListCallback: (deviceList) => dispatch(actions.localAudioOutputListReceived(deviceList)),
        messageReceivedCallback: messageReceived,
        photoRequestReceived: (id) =>
            dispatch(actions.photoRequestReceived(id, getState().localizations?.takingPhoto ?? "Taking Photo")),
        photoTakenCallback: (id, photo) => dispatch(actions.photoTaken(id, photo)),
        setSessionActive: (isActive) => dispatch(actions.setOtherPartyActive(isActive)),
        receivedPhotoUrlFromClient: (id, url, thumbnailUrl) =>
            dispatch(actions.receivedPhotoUrl(id, url, thumbnailUrl, getState().order.id)),
        receivedVideoFrameTagPhotoUrlFromMentor: (orderId, photo) =>
            dispatch(actions.receivedVideoFrameTagPhotoUrl(orderId, photo)),
        videoSessionEndedCallback: () => dispatch(actions.sessionEnded(getState().order.id)),
        updateNetworkStatus: (connected) => {
            dispatch(actions.networkStatusUpdated(getState().order.id, connected));
        },
        updateCameraBusyStatus: (busy) => dispatch(actions.toggleCameraBusy(busy)),
        confirmClientAudioMuteToggle: () => dispatch(actions.setAudioMuteConfirmState(true)),
        confirmClientCameraMuteToggle: () => dispatch(actions.setVideoMuteConfirmState(true)),
        confirmClientCameraChange: (deviceId) => dispatch(actions.setChangeCameraConfirmState(deviceId)),
        removeDeletedPhoto: (photoId) => dispatch(actions.photoRemoved(photoId)),
        recordingToggled: (isRecording) => dispatch(actions.recordingToggled(isRecording)),
        videoAIFrameSampleRate: guideStreamSettings.videoAIFrameSampleIntervalMilliseconds,
        sendClientConnectionQualityWarning: () => dispatch(actions.issueClientConnectionQualityWarning()),
        sendClientAudioMuteUpdated: (muted) => dispatch(actions.sendClientAudioMuteUpdated(muted)),
    } as LiveSwitchMiddlewareConnector;

    liveSwitchApp.registerMiddlewareCallbacks(connector);

    return (next: any) => (action: actions.LiveSwitchAction) => {
        switch (action.type) {
            case types.TAKE_LOCAL_MEDIA_PHOTO: {
                const id = action.photoId;
                liveSwitchApp.takePhoto(id);
                break;
            }
            case types.SEND_CHAT: {
                let chat = action.chat as Chat;
                liveSwitchApp.sendMessage("chat:" + JSON.stringify(chat));
                break;
            }
            case types.COMMAND_MUTE_REMOTE_CAMERA: {
                liveSwitchApp.sendToggleMuteCameraCommand();
                break;
            }
            case types.COMMAND_SESSION_ENDED: {
                liveSwitchApp.sendSessionEndedCommand();
                break;
            }
            case types.COMMAND_MUTE_REMOTE_AUDIO: {
                liveSwitchApp.sendToggleMuteAudioCommand();
                break;
            }
            case types.TOGGLE_LOCAL_VIDEO: {
                liveSwitchApp.toggleLocalMediaVideoMute();
                break;
            }
            case types.TOGGLE_LOCAL_AUDIO: {
                liveSwitchApp.toggleLocalMediaAudioMute();
                break;
            }
            case types.CLIENT_AUDIO_MUTE_UPDATED: {
                liveSwitchApp.sendClientMuteChangedCommand(action.muted);
                break;
            }
            case types.COMMAND_SEND_PHOTO_URL: {
                liveSwitchApp.sendPhotoURLCommand(action.id, action.url, action.thumbnailUrl);
                break;
            }
            case types.COMMAND_SEND_VIDEO_FRAME_TAG_PHOTO_URL: {
                liveSwitchApp.sendVideoFrameTagPhotoUrlCommand(action.orderId, action.photo);
                break;
            }
            case types.COMMAND_CHANGE_REMOTE_CAMERA: {
                liveSwitchApp.sendToggleCameraCommand(action.id, action.name);
                break;
            }
            case types.COMMAND_TOGGLE_RECORDING: {
                liveSwitchApp.sendToggleRecordingCommand(action.isRecording);
                break;
            }
            case types.COMMAND_REQUEST_PHOTO: {
                liveSwitchApp.sendRequestPhotoCommand(action.id);
                break;
            }
            case types.SET_LOCAL_VIDEO_INPUT: {
                liveSwitchApp.setActiveLocalCamera(action.id);
                break;
            }
            case types.SET_LOCAL_AUDIO_OUTPUT: {
                liveSwitchApp.setLocalAudioOutput(action.id, action.name);
                break;
            }
            case types.SET_LOCAL_AUDIO_INPUT: {
                liveSwitchApp.setActiveLocalAudio(action.id);
                break;
            }
            case types.COMMAND_SEND_PHOTO_REMOVED: {
                liveSwitchApp.sendRemoveDeletedPhotoCommand(action.photoId);
                break;
            }
            case types.SET_CANVAS_REDUCER_QUALITY: {
                liveSwitchApp.setQuality(action.quality);
                break;
            }
            case types.SEND_CANVAS_QUALITY_COMMAND: {
                liveSwitchApp.sendCanvasQualityCommand(action.quality);
                break;
            }

            case types.ISSUE_CLIENT_CONNECTION_QUALITY_WARNING: {
                liveSwitchApp.sendClientConnectionQualityWarningCommand();
                break;
            }
            case types.CANCEL_CLIENT_CONNECTION_QUALITY_WARNING: {
                liveSwitchApp.cancelClientConnectionQualityWarningCommand();
                break;
            }
            case types.GET_AUDIO_OUTPUT_DEVICES: {
                liveSwitchApp.requestAudioOutputDevices();
                break;
            }
        }
        //         case types.SIGNALR_CONNECT:
        //             signalRStart(connection, hubProxy, dispatch, getState);
        //             signalRTrackErrors(connection, dispatch);
        //             break;
        //         case types.SIGNALR_DISCONNECT:
        //             signalRStop(connection, dispatch);
        //             break;
        //         case types.SIGNALR_INVOKE_MESSAGE:
        //             hubProxy.invoke(
        //                 action.payload.event,
        //                 action.payload.payload
        //             );
        //             break;
        //         default:
        //         //Do nothing?

        // }
        return next(action);
    };
}
